import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAnnouncements } from '../../hooks/useApi'
import { useAppStore } from '../../store/useAppStore'
import type { AnnouncementWithCard } from '../../types/api'
import './RecipeOfTheDay.css'
import Claude from '../../assets/images/cloud.svg'
import logoLight from '../../assets/images/logoLight.svg'
import wheelBanner from '../../assets/images/wheelBunner.png'

interface CustomBanner {
  isCustomBanner: true
  id: string
  bannerType?: 'subscription' | 'fortune-wheel'
}

type SlideItem = AnnouncementWithCard | CustomBanner

const RecipeOfTheDay = ({ hasSubscription }: { hasSubscription: boolean }) => {
  const navigate = useNavigate()
  const { data: announcements, isLoading } = useAnnouncements()
  const { user } = useAppStore()
  console.log('🚀 ~ RecipeOfTheDay ~ user:', user)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const intervalRef = useRef<number | null>(null)
  const resumeTimeoutRef = useRef<number | null>(null)

  // Определяем тип устройства
  const isDesktop = !('ontouchstart' in window) || window.innerWidth > 768

  // Формируем список слайдов с учетом подписки
  const serverSlides: AnnouncementWithCard[] =
    announcements?.announcements || []
  const allSlides: SlideItem[] = hasSubscription
    ? [
        // Кастомный баннер колеса фортуны для пользователей с подпиской (временно скрыт)
        // {
        //   isCustomBanner: true,
        //   id: 'fortune-wheel-banner',
        //   bannerType: 'fortune-wheel',
        // } as CustomBanner,
        ...serverSlides,
      ]
    : [
        // Кастомный баннер для пользователей без подписки
        {
          isCustomBanner: true,
          id: 'subscription-banner',
          bannerType: 'subscription',
        } as CustomBanner,
        ...serverSlides,
      ]

  // Единый useEffect для управления автопроигрыванием
  useEffect(() => {
    // Очистка предыдущих таймеров
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current)
      resumeTimeoutRef.current = null
    }

    if (allSlides.length > 1) {
      if (isAutoPlaying) {
        // Запускаем автопроигрывание
        intervalRef.current = setInterval(() => {
          setCurrentSlide(prev => (prev + 1) % allSlides.length)
        }, 5000)
      } else {
        // Возобновляем автопроигрывание через 10 секунд
        resumeTimeoutRef.current = setTimeout(() => {
          setIsAutoPlaying(true)
        }, 10000)
      }
    }

    // Очистка при размонтировании или изменении зависимостей
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current)
        resumeTimeoutRef.current = null
      }
    }
  }, [isAutoPlaying, allSlides.length])

  if (isLoading || allSlides.length === 0) {
    return (
      <section className="recipe-of-the-day">
        <div className="recipe-slider-container">
          <div className="recipe-slide-skeleton">
            <div className="recipe-slide-skeleton-content">
              <div className="recipe-skeleton-badge"></div>
              <div className="recipe-skeleton-title"></div>
            </div>
          </div>

          <div className="recipe-slide-dots">
            <div className="recipe-dot-skeleton active"></div>
            <div className="recipe-dot-skeleton"></div>
            <div className="recipe-dot-skeleton"></div>
          </div>
        </div>
      </section>
    )
  }

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % allSlides.length)
  }

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + allSlides.length) % allSlides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  const pauseAutoPlay = () => {
    setIsAutoPlaying(false)
  }

  const resumeAutoPlay = () => {
    if (allSlides.length > 1) {
      setIsAutoPlaying(true)
    }
  }

  // Нет модального окна: при клике на кастомный баннер переходим на страницу подписки

  const handlePrevClick = () => {
    prevSlide()
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  const handleNextClick = () => {
    nextSlide()
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  // Минимальное расстояние для распознавания свайпа
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
    setIsAutoPlaying(false) // Останавливаем автопроигрывание при touch
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      nextSlide()
    } else if (isRightSwipe) {
      prevSlide()
    }
  }

  return (
    <section className="recipe-of-the-day">
      <div
        className="recipe-slider-container"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
      >
        {/* Кнопки навигации для PC */}
        {isDesktop && allSlides.length > 1 && (
          <>
            <button
              className="recipe-nav-button recipe-nav-button-prev"
              onClick={handlePrevClick}
              aria-label="Предыдущий слайд"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="recipe-nav-button recipe-nav-button-next"
              onClick={handleNextClick}
              aria-label="Следующий слайд"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        <div
          className="recipe-slides-wrapper"
          style={{
            transform: `translateX(-${currentSlide * (100 / allSlides.length)}%)`,
            width: `${allSlides.length * 100}%`,
          }}
        >
          {allSlides.map(slide => {
            // Кастомный баннер
            if ('isCustomBanner' in slide && slide.isCustomBanner) {
              const customSlide = slide as CustomBanner

              // Баннер колеса фортуны для пользователей с подпиской
              if (customSlide.bannerType === 'fortune-wheel') {
                return (
                  <div
                    key={slide.id}
                    className="wheel-slide wheel-slide-fortune-wheel-banner"
                    onClick={() =>
                      navigate('/fortune-wheel', {
                        state: { showLoader: true },
                      })
                    }
                    style={{
                      width: `${100 / allSlides.length}%`,
                    }}
                  >
                    <img src={wheelBanner} alt="" className="wheel-baner" />
                    <div className="wheel-slide-subscription-banner-content">
                      <div className="subscription-banner-text">
                        <p className="wheel-banner-text">Новогодний розыгрыш</p>
                        <p className="wheel-banner-subtitle">
                          Крути колесо и выигрывай ценные призы
                        </p>
                      </div>
                    </div>
                  </div>
                )
              }

              // Баннер подписки для пользователей без подписки
              return (
                <div
                  key={slide.id}
                  className="recipe-slide recipe-slide-subscription-banner"
                  onClick={() => navigate('/subscribe')}
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  <img
                    src={Claude}
                    alt="Subscription Banner"
                    className="claude claude01"
                  />
                  <img
                    src={Claude}
                    alt="Subscription Banner"
                    className="claude claude02"
                  />
                  <img
                    src={Claude}
                    alt="Subscription Banner"
                    className="claude claude03"
                  />
                  <div className="recipe-slide-subscription-banner-content">
                    <p className="subscription-banner-top-title">Подписка</p>
                    <div className="subscription-banner-text">
                      {/* Здесь будет контент вашего баннера */}
                      <img src={logoLight} alt="Logo" className="logo-light" />
                      <p className="subscription-banner-subtitle">
                        Получи доступ к 300+ рецептам и обучающим материалам
                      </p>
                    </div>
                  </div>
                </div>
              )
            }

            // Обычные слайды с сервера
            const announcement = slide as AnnouncementWithCard

            // Определяем, куда ведет слайд
            const slideContent = (
              <>
                <div
                  className="recipe-slide-background"
                  style={{
                    backgroundImage: `url(${announcement.banner_url})`,
                  }}
                />
                <div className="recipe-slide-overlay" />
                <div className="recipe-slide-content">
                  <div className="recipe-slide-badge">
                    {announcement.announcement_text}
                  </div>
                  <h2 className="recipe-slide-title">
                    {announcement.announcement_subtitle}
                  </h2>
                </div>
              </>
            )

            // Если есть link_url, то это внешняя ссылка
            if (announcement.link_url) {
              return (
                <a
                  key={
                    announcement.card_id || `link-${announcement.sort_order}`
                  }
                  href={announcement.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="recipe-slide"
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  {slideContent}
                </a>
              )
            }

            // Если есть card_id, то это ссылка на карточку
            if (announcement.card_id) {
              return (
                <Link
                  key={announcement.card_id}
                  to={`/card/${announcement.card_id}`}
                  className="recipe-slide"
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  {slideContent}
                </Link>
              )
            }

            // Если нет ни link_url, ни card_id, то ведем на страницу подписки
            return (
              <Link
                key={`announcement-${announcement.sort_order}`}
                to="/subscribe"
                className="recipe-slide"
                style={{
                  width: `${100 / allSlides.length}%`,
                }}
              >
                {slideContent}
              </Link>
            )
          })}
        </div>

        {/* Навигационные точки */}
        {allSlides.length > 1 && (
          <div className="recipe-slide-dots">
            {allSlides.map((_, index) => (
              <button
                key={index}
                className={`recipe-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Перейти к слайду ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Клик по кастомному баннеру ведет на /subscribe */}
    </section>
  )
}

export default RecipeOfTheDay
